import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, Link, useNavigate } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaCreditCard,
  FaClipboardCheck,
  FaArrowLeft,
  FaArrowRight,
  FaShieldAlt,
  FaTruck,
  FaUndo,
  FaCheck,
  FaMoneyBillWave,
  FaUniversity,
  FaWallet,
  FaPlus,
  FaTrash,
  FaLock,
  FaBoxOpen,
  FaShoppingBag,
} from "react-icons/fa";
import Header from "../../components/header/header";
import Footer from "../../components/footer/footer";
import "./chckout.css";

const API_URL = process.env.REACT_APP_API_URL;

const steps = [
  { label: "Shipping", icon: <FaMapMarkerAlt /> },
  { label: "Review Order", icon: <FaClipboardCheck /> },
];

const pageVariants = {
  hidden: { opacity: 0, y: 25 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      when: "beforeChildren",
      staggerChildren: 0.07,
    },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 15 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35 },
  },
};

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 45 : -45,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.3, ease: "easeOut" },
  },
  exit: (direction) => ({
    x: direction > 0 ? -45 : 45,
    opacity: 0,
    transition: { duration: 0.2 },
  }),
};

const emptyAddress = {
  label: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
};

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
};

const paymentOptions = [
  {
    key: "card",
    icon: <FaCreditCard />,
    label: "Cards",
    desc: "Credit and debit cards",
  },
  {
    key: "upi",
    icon: <FaWallet />,
    label: "UPI",
    desc: "Google Pay, PhonePe, Paytm",
  },
  {
    key: "netbanking",
    icon: <FaUniversity />,
    label: "Net Banking",
    desc: "Pay through your bank",
  },
  {
    key: "cod",
    icon: <FaMoneyBillWave />,
    label: "Cash on Delivery",
    desc: "Pay when your order arrives",
  },
];

const Checkout = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [cartLoading, setCartLoading] = useState(true);

  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);

  const [paymentMethod, setPaymentMethod] = useState("card");

  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);

  const [newAddress, setNewAddress] = useState(emptyAddress);
  const [form, setForm] = useState(emptyForm);

  // Load saved addresses.
  useEffect(() => {
    try {
      const saved = localStorage.getItem("checkoutAddresses");

      if (saved) {
        const parsed = JSON.parse(saved);

        if (Array.isArray(parsed)) {
          setAddresses(parsed);
        }
      }
    } catch (error) {
      console.error("Unable to load saved addresses:", error);
    }
  }, []);

  // Load cart items.
  useEffect(() => {
    let active = true;

    const loadCart = async () => {
      setCartLoading(true);

      try {
        const token = localStorage.getItem("token");
        const guestId = localStorage.getItem("guestId");

        const query = guestId ? `?guestId=${encodeURIComponent(guestId)}` : "";

        const response = await fetch(`${API_URL}/cart${query}`, {
          headers: {
            ...(token && {
              Authorization: `Bearer ${token}`,
            }),
          },
        });

        if (!response.ok) {
          throw new Error("Unable to load your cart.");
        }

        const data = await response.json();
        const map = new Map();

        (data.items || []).forEach((item) => {
          const key = [
            item.productId,
            item.color || "",
            item.memory || "",
            item.storage || "",
          ].join("_");

          if (map.has(key)) {
            map.get(key).qty += Number(item.quantity);
          } else {
            map.set(key, {
              id: key,
              productId: item.productId,
              name: item.name,
              price: Number(item.price),
              originalPrice: Number(item.originalPrice) || Number(item.price),
              qty: Number(item.quantity),
              image: item.image,
              color: item.color || "",
              memory: item.memory || "",
              storage: item.storage || "",
              variant: [item.color, item.memory, item.storage]
                .filter(Boolean)
                .join(" · "),
            });
          }
        });

        if (active) {
          setCartItems(Array.from(map.values()));
        }
      } catch (error) {
        console.error("Checkout cart load error:", error);

        if (active) {
          alert(error.message || "Unable to load your cart.");
        }
      } finally {
        if (active) {
          setCartLoading(false);
        }
      }
    };

    loadCart();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname]);

  // Form input handler.
  const handleInput = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleAddressInput = (event) => {
    const { name, value } = event.target;

    setNewAddress((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  // Save addresses safely.
  const persistAddresses = (updated) => {
    setAddresses(updated);
    localStorage.setItem("checkoutAddresses", JSON.stringify(updated));
  };

  const handleSaveAddress = () => {
    if (
      !newAddress.address.trim() ||
      !newAddress.city.trim() ||
      !newAddress.state.trim() ||
      !newAddress.pincode.trim()
    ) {
      alert("Please fill in Address, City, State and Pincode.");
      return;
    }

    const addressToSave = {
      ...newAddress,
      label: newAddress.label.trim() || "Home",
      address: newAddress.address.trim(),
      city: newAddress.city.trim(),
      state: newAddress.state.trim(),
      pincode: newAddress.pincode.trim(),
    };

    const updated = [...addresses, addressToSave];

    persistAddresses(updated);
    setSelectedAddress(addressToSave);
    setNewAddress(emptyAddress);
    setShowAddressForm(false);
  };

  const handleDeleteAddress = (event, address) => {
    event.stopPropagation();

    const updated = addresses.filter((item) => item !== address);

    persistAddresses(updated);

    if (selectedAddress === address) {
      setSelectedAddress(null);
    }
  };

  const validateShipping = () => {
    if (!selectedAddress) {
      alert("Please select or add a shipping address.");
      return false;
    }

    if (!form.firstName.trim() || !form.email.trim() || !form.phone.trim()) {
      alert("Please fill in First Name, Email and Phone.");
      return false;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(form.email.trim())) {
      alert("Please enter a valid email address.");
      return false;
    }

    if (!/^[0-9+\s()-]{7,20}$/.test(form.phone.trim())) {
      alert("Please enter a valid phone number.");
      return false;
    }

    return true;
  };

  const goNext = () => {
    if (currentStep === 0) {
      if (!validateShipping()) return;

      setDirection(1);
      setCurrentStep(1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goBack = () => {
    if (currentStep > 0) {
      setDirection(-1);
      setCurrentStep(0);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // =====================================
  // UPDATED: Order totals (with shipping logic)
  // =====================================
  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.qty,
    0,
  );
  
  const totalOriginal = cartItems.reduce(
    (sum, item) => sum + item.originalPrice * item.qty,
    0,
  );

  const discount = Math.max(0, totalOriginal - subtotal);
  
  // Matches the Cart page logic: Free over 50,000, otherwise 499. (Free if cart is empty)
  const shipping = subtotal === 0 || subtotal > 50000 ? 0 : 499;
  
  const total = subtotal + shipping;

  const fmt = (amount) =>
    "₹" +
    Number(amount || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    });

  // Prevent accidental double submissions.
  const handlePlaceOrder = async () => {
    if (isPlacingOrder) return;

    if (!validateShipping()) {
      setCurrentStep(0);
      setDirection(-1);
      return;
    }

    if (cartItems.length === 0) {
      alert("Your cart is empty.");
      return;
    }

    if (!API_URL) {
      alert("API URL is not configured.");
      return;
    }

    setIsPlacingOrder(true);

    const token = localStorage.getItem("token");
    const guestId = localStorage.getItem("guestId");

    const orderPayload = {
      guestId,
      items: cartItems.map((item) => ({
        productId: item.productId,
        name: item.name,
        price: item.price,
        quantity: item.qty,
        color: item.color,
        memory: item.memory,
        storage: item.storage,
      })),
      subtotal,
      shipping,
      discount,
      total,
      shippingAddress: {
        ...selectedAddress,
        name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
      },
      paymentMethod: paymentMethod.toUpperCase(),
    };

    const authHeaders = {
      "Content-Type": "application/json",
      ...(token && {
        Authorization: `Bearer ${token}`,
      }),
    };

    const readResponse = async (response, fallbackMessage) => {
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || fallbackMessage);
      }

      return data;
    };

    const finishOrder = (data) => {
      localStorage.setItem(
        "lastOrder",
        JSON.stringify({
          orderNumber: data.orderNumber,
          total,
        }),
      );

      navigate("/order-success", {
        state: {
          orderNumber: data.orderNumber,
          total,
        },
      });
    };

    try {
      // CASH ON DELIVERY.
      if (paymentMethod === "cod") {
        const response = await fetch(`${API_URL}/orders/place`, {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify(orderPayload),
        });

        const data = await readResponse(
          response,
          "Unable to place your order.",
        );

        finishOrder(data);
        return;
      }

      // ONLINE PAYMENT: CREATE RAZORPAY ORDER.
      if (!window.Razorpay) {
        throw new Error(
          "Razorpay is not loaded. Please refresh and try again.",
        );
      }

      const key = process.env.REACT_APP_RAZORPAY_KEY_ID;

      if (!key) {
        throw new Error(
          "Razorpay key is missing from your environment configuration.",
        );
      }

      const orderResponse = await fetch(`${API_URL}/orders/razorpay/order`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ amount: total }),
      });

      const razorpayData = await readResponse(
        orderResponse,
        "Failed to initialize Razorpay.",
      );

      if (!razorpayData.order?.id) {
        throw new Error("Invalid Razorpay order response.");
      }

      // Keep loading active while Razorpay is open.
      const options = {
        key,
        amount: razorpayData.order.amount,
        currency: razorpayData.order.currency || "INR",
        name: "Crescent World",
        description: "Order Payment",
        order_id: razorpayData.order.id,

        prefill: {
          name: `${form.firstName} ${form.lastName}`.trim(),
          email: form.email.trim(),
          contact: form.phone.trim(),
        },

        theme: {
          color: "#6254e8",
        },

        modal: {
          ondismiss: () => {
            setIsPlacingOrder(false);
          },
        },

        handler: async (response) => {
          try {
            // VERIFY PAYMENT SIGNATURE.
            const verifyResponse = await fetch(
              `${API_URL}/orders/razorpay/verify`,
              {
                method: "POST",
                headers: authHeaders,
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                }),
              },
            );

            const verifyData = await readResponse(
              verifyResponse,
              "Payment verification failed.",
            );

            if (!verifyData.success) {
              throw new Error(
                "Payment could not be verified. Please contact support.",
              );
            }

            // SAVE ORDER AFTER VERIFIED PAYMENT.
            const placeResponse = await fetch(`${API_URL}/orders/place`, {
              method: "POST",
              headers: authHeaders,
              body: JSON.stringify(orderPayload),
            });

            const orderData = await readResponse(
              placeResponse,
              "Payment succeeded, but order creation failed. Contact support before retrying payment.",
            );

            finishOrder(orderData);
          } catch (error) {
            console.error("Payment/order error:", error);
            alert(error.message || "Unable to complete your order.");
            setIsPlacingOrder(false);
          }
        },
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on("payment.failed", (response) => {
        alert(
          response.error?.description || "Payment failed. Please try again.",
        );

        setIsPlacingOrder(false);
      });

      razorpay.open();
    } catch (error) {
      console.error("Order error:", error);
      alert(error.message || "Unable to place your order.");
      setIsPlacingOrder(false);
    }
  };

  // =====================================
  // SHIPPING STEP
  // =====================================

  const shippingStepContent = (
    <motion.div
      key="shipping"
      custom={direction}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
    >
      <div className="checkout-section">
        <div className="section-header">
          <div className="section-icon address">
            <FaMapMarkerAlt />
          </div>

          <div>
            <h5 className="section-title">Shipping Address</h5>
            <p className="section-subtitle">
              Where should we deliver your order?
            </p>
          </div>
        </div>

        {addresses.length === 0 && !showAddressForm && (
          <div className="checkout-empty-address">
            <FaMapMarkerAlt />
            <div>
              <strong>No saved addresses yet</strong>
              <p>Add a delivery address to continue.</p>
            </div>
          </div>
        )}

        <div className="saved-address-list">
          {addresses.map((address, index) => {
            const isSelected = selectedAddress === address;

            return (
              <motion.div
                key={`${address.label}-${index}`}
                className={`saved-address ${isSelected ? "selected" : ""}`}
                onClick={() => setSelectedAddress(address)}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.995 }}
              >
                <span className="address-radio">
                  {isSelected && <FaCheck />}
                </span>

                <div className="saved-address-content">
                  <div className="saved-address-heading">
                    <strong>{address.label || "Address"}</strong>

                    {isSelected && (
                      <span className="address-selected-badge">Selected</span>
                    )}
                  </div>

                  <p>
                    {address.address}, {address.city}, {address.state} -{" "}
                    {address.pincode}
                  </p>
                </div>

                <button
                  type="button"
                  className="delete-address-btn"
                  aria-label="Delete address"
                  title="Delete address"
                  onClick={(event) => handleDeleteAddress(event, address)}
                >
                  <FaTrash />
                </button>
              </motion.div>
            );
          })}
        </div>

        {!showAddressForm && (
          <motion.button
            type="button"
            className="add-address-btn"
            onClick={() => setShowAddressForm(true)}
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.98 }}
          >
            <FaPlus />
            Add a new address
          </motion.button>
        )}

        <AnimatePresence>
          {showAddressForm && (
            <motion.div
              className="new-address-form"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h6>Add New Address</h6>

              <div className="row g-3">
                <div className="col-12">
                  <label className="input-label">Address Label</label>
                  <input
                    name="label"
                    className="checkout-input"
                    placeholder="Home / Office"
                    value={newAddress.label}
                    onChange={handleAddressInput}
                  />
                </div>

                <div className="col-12">
                  <label className="input-label">Full Address *</label>
                  <input
                    name="address"
                    className="checkout-input"
                    placeholder="House number, street, area"
                    value={newAddress.address}
                    onChange={handleAddressInput}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="input-label">City *</label>
                  <input
                    name="city"
                    className="checkout-input"
                    placeholder="City"
                    value={newAddress.city}
                    onChange={handleAddressInput}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="input-label">State *</label>
                  <input
                    name="state"
                    className="checkout-input"
                    placeholder="State"
                    value={newAddress.state}
                    onChange={handleAddressInput}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="input-label">Pincode *</label>
                  <input
                    name="pincode"
                    className="checkout-input"
                    placeholder="Pincode"
                    inputMode="numeric"
                    maxLength={10}
                    value={newAddress.pincode}
                    onChange={handleAddressInput}
                  />
                </div>
              </div>

              <div className="address-form-actions">
                <button
                  type="button"
                  className="next-btn"
                  onClick={handleSaveAddress}
                >
                  Save Address
                </button>

                <button
                  type="button"
                  className="back-btn"
                  onClick={() => {
                    setShowAddressForm(false);
                    setNewAddress(emptyAddress);
                  }}
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="checkout-section">
        <div className="section-header">
          <div className="section-icon address">
            <FaClipboardCheck />
          </div>

          <div>
            <h5 className="section-title">Contact Information</h5>
            <p className="section-subtitle">
              We will use these details for order updates.
            </p>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-12 col-md-6">
            <label className="input-label">First Name *</label>
            <input
              type="text"
              name="firstName"
              autoComplete="given-name"
              className="checkout-input"
              placeholder="Enter first name"
              value={form.firstName}
              onChange={handleInput}
            />
          </div>

          <div className="col-12 col-md-6">
            <label className="input-label">Last Name</label>
            <input
              type="text"
              name="lastName"
              autoComplete="family-name"
              className="checkout-input"
              placeholder="Enter last name"
              value={form.lastName}
              onChange={handleInput}
            />
          </div>

          <div className="col-12 col-md-6">
            <label className="input-label">Email *</label>
            <input
              type="email"
              name="email"
              autoComplete="email"
              className="checkout-input"
              placeholder="you@example.com"
              value={form.email}
              onChange={handleInput}
            />
          </div>

          <div className="col-12 col-md-6">
            <label className="input-label">Phone *</label>
            <input
              type="tel"
              name="phone"
              autoComplete="tel"
              inputMode="tel"
              className="checkout-input"
              placeholder="Enter phone number"
              value={form.phone}
              onChange={handleInput}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );

  // =====================================
  // ORDER REVIEW STEP
  // =====================================

  const reviewStepContent = (
    <motion.div
      key="review"
      custom={direction}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
    >
      <div className="checkout-section">
        <div className="section-header">
          <div className="section-icon review">
            <FaShoppingBag />
          </div>

          <div>
            <h5 className="section-title">Review Your Order</h5>
            <p className="section-subtitle">
              Check your items before placing the order.
            </p>
          </div>
        </div>

        {cartItems.map((item) => (
          <motion.div className="review-item" key={item.id} variants={fadeUp}>
            <img src={item.image} alt={item.name} className="review-item-img" />

            <div className="review-item-details">
              <div className="review-item-name">{item.name}</div>

              {item.variant && (
                <div className="review-item-variant">{item.variant}</div>
              )}

              <div className="review-item-unit-price">
                {fmt(item.price)} each
              </div>
            </div>

            <span className="review-item-qty">× {item.qty}</span>

            <span className="review-item-price">
              {fmt(item.price * item.qty)}
            </span>
          </motion.div>
        ))}

        {cartItems.length === 0 && !cartLoading && (
          <div className="checkout-empty-address">
            <FaBoxOpen />
            <div>
              <strong>Your cart is empty</strong>
              <p>Add some products before checking out.</p>
              <Link to="/cart">Return to cart</Link>
            </div>
          </div>
        )}
      </div>

      <div className="checkout-section">
        <div className="section-header">
          <div className="section-icon address">
            <FaMapMarkerAlt />
          </div>

          <div>
            <h5 className="section-title">Delivery Details</h5>
            <p className="section-subtitle">
              Your order will be shipped to this address.
            </p>
          </div>
        </div>

        <div className="review-delivery-card">
          <div className="review-delivery-icon">
            <FaMapMarkerAlt />
          </div>

          <div className="review-delivery-details">
            <strong>{selectedAddress?.label || "Delivery Address"}</strong>

            <p>
              {selectedAddress
                ? `${selectedAddress.address}, ${selectedAddress.city}, ${selectedAddress.state} - ${selectedAddress.pincode}`
                : "No address selected"}
            </p>

            <p>
              {form.firstName} {form.lastName}
            </p>
            <p>{form.email}</p>
            <p>{form.phone}</p>
          </div>

          <button type="button" className="edit-details-btn" onClick={goBack}>
            Edit
          </button>
        </div>
      </div>

      <div className="checkout-section">
        {paymentMethod !== "cod" && (
          <div className="checkout-payment-note">
            <FaShieldAlt />
            <p>
              Secure online payment powered by Razorpay. Complete your payment
              in the secure payment window.
            </p>
          </div>
        )}

        {paymentMethod === "cod" && (
          <div className="checkout-payment-note cod-note">
            <FaMoneyBillWave />
            <p>Pay for your order when it is delivered to your address.</p>
          </div>
        )}

        <motion.button
          type="button"
          className="place-order-btn mt-4"
          onClick={handlePlaceOrder}
          disabled={isPlacingOrder || cartLoading || cartItems.length === 0}
          whileHover={!isPlacingOrder && cartItems.length > 0 ? { y: -2 } : {}}
          whileTap={{ scale: 0.99 }}
        >
          {isPlacingOrder ? (
            <>
              <span className="checkout-spinner" />
              Processing...
            </>
          ) : (
            <>
              <FaLock />
              Place Order — {fmt(total)}
              <FaArrowRight />
            </>
          )}
        </motion.button>

        <div className="checkout-trust">
          <span>
            <FaShieldAlt />
            Secure Checkout
          </span>
          <span>
            <FaTruck />
            {shipping === 0 ? "Free Shipping" : `Shipping: ${fmt(shipping)}`}
          </span>
          <span>
            <FaUndo />
            Easy Returns
          </span>
        </div>
      </div>
    </motion.div>
  );

  const stepComponents = [shippingStepContent, reviewStepContent];

  return (
    <div className="checkout-root">
      <Header />

      <motion.main
        className="checkout-page"
        variants={pageVariants}
        initial="hidden"
        animate="show"
      >
        <Container>
          <motion.div className="checkout-heading" variants={fadeUp}>
            <span className="checkout-eyebrow">
              <FaLock /> SAFE & SECURE CHECKOUT
            </span>

            <h1>
              Complete Your <span>Order</span>
            </h1>

            <p>Just a few steps away from getting your order delivered.</p>
          </motion.div>

          <motion.div className="checkout-stepper" variants={fadeUp}>
            {steps.map((step, index) => (
              <React.Fragment key={step.label}>
                <motion.div
                  className={`step ${index === currentStep ? "active" : ""} ${
                    index < currentStep ? "completed" : ""
                  }`}
                  onClick={() => {
                    if (index < currentStep) {
                      goBack();
                    }
                  }}
                  role={index < currentStep ? "button" : undefined}
                  tabIndex={index < currentStep ? 0 : undefined}
                  onKeyDown={(event) => {
                    if (
                      index < currentStep &&
                      (event.key === "Enter" || event.key === " ")
                    ) {
                      goBack();
                    }
                  }}
                >
                  <motion.span
                    className="step-num"
                    animate={
                      index === currentStep
                        ? { scale: [1, 1.08, 1] }
                        : { scale: 1 }
                    }
                    transition={{ duration: 0.4 }}
                  >
                    {index < currentStep ? <FaCheck /> : step.icon}
                  </motion.span>

                  <span className="step-label">{step.label}</span>
                </motion.div>

                {index < steps.length - 1 && (
                  <div
                    className={`step-line ${index < currentStep ? "done" : ""}`}
                  />
                )}
              </React.Fragment>
            ))}
          </motion.div>

          <Row className="checkout-layout">
            <Col lg={8}>
              <div className="checkout-main-column">
                {cartLoading ? (
                  <div className="checkout-loading">
                    <span className="checkout-spinner" />
                    <p>Loading your order...</p>
                  </div>
                ) : (
                  <>
                    <AnimatePresence mode="wait" custom={direction}>
                      {stepComponents[currentStep]}
                    </AnimatePresence>

                    {currentStep === 0 && (
                      <motion.div
                        className="checkout-nav"
                        variants={fadeUp}
                        initial="hidden"
                        animate="show"
                      >
                        <Link to="/cart" className="back-btn">
                          <FaArrowLeft />
                          Back to Cart
                        </Link>

                        <motion.button
                          type="button"
                          className="next-btn"
                          onClick={goNext}
                          whileHover={{
                            scale: 1.02,
                            y: -2,
                          }}
                          whileTap={{
                            scale: 0.98,
                          }}
                        >
                          Review Order
                          <FaArrowRight />
                        </motion.button>
                      </motion.div>
                    )}

                    {currentStep === 1 && (
                      <motion.div
                        className="checkout-nav"
                        variants={fadeUp}
                        initial="hidden"
                        animate="show"
                      >
                        <motion.button
                          type="button"
                          className="back-btn"
                          onClick={goBack}
                          whileHover={{
                            scale: 1.02,
                          }}
                          whileTap={{
                            scale: 0.98,
                          }}
                        >
                          <FaArrowLeft />
                          Back to Shipping
                        </motion.button>
                      </motion.div>
                    )}
                  </>
                )}
              </div>
            </Col>

            <Col lg={4}>
              <motion.aside className="checkout-summary" variants={fadeUp}>
                <div className="summary-heading">
                  <div>
                    <h5 className="checkout-summary-title">Order Summary</h5>
                    <p>
                      {cartItems.length} product
                      {cartItems.length !== 1 ? "s" : ""}
                    </p>
                  </div>

                  <div className="summary-bag-icon">
                    <FaShoppingBag />
                  </div>
                </div>

                {cartItems.length === 0 && !cartLoading ? (
                  <p className="summary-empty">Your cart is empty.</p>
                ) : (
                  cartItems.map((item) => (
                    <div key={item.id} className="summary-product">
                      <div className="summary-product-image">
                        <img src={item.image} alt={item.name} />
                        <span>{item.qty}</span>
                      </div>

                      <div className="summary-product-info">
                        <div>{item.name}</div>
                        {item.variant && <small>{item.variant}</small>}
                        <small>{fmt(item.price)} each</small>
                      </div>

                      <strong>{fmt(item.price * item.qty)}</strong>
                    </div>
                  ))
                )}

                <div className="summary-divider" />

                <div className="summary-row">
                  <span className="label">Subtotal</span>
                  <span className="value">{fmt(subtotal)}</span>
                </div>

                {discount > 0 && (
                  <div className="summary-row">
                    <span className="label">Discount</span>
                    <span className="value discount">− {fmt(discount)}</span>
                  </div>
                )}

                <div className="summary-row">
                  <span className="label">Shipping</span>
                  <span className={`value ${shipping === 0 ? "shipping-free" : ""}`}>
                    {shipping === 0 ? "FREE" : fmt(shipping)}
                  </span>
                </div>

                <div className="summary-divider" />

                <div className="summary-total">
                  <span>Total</span>
                  <span>{fmt(total)}</span>
                </div>

                <div className="summary-secure-note">
                  <FaShieldAlt />
                  <span>Your information is protected during checkout.</span>
                </div>

                <Link to="/cart" className="summary-edit-cart">
                  <FaArrowLeft />
                  Edit Cart
                </Link>
              </motion.aside>
            </Col>
          </Row>
        </Container>
      </motion.main>

      <Footer />
    </div>
  );
};

export default Checkout;